import { useEffect } from "react";

import { ApolloQueryResult, NetworkStatus, useLazyQuery } from "@apollo/client";
import uniqBy from "lodash/uniqBy";

import {
  GetNodesWithoutDeviceByCustomerQuery,
  GetNodesWithoutDeviceByCustomerQueryVariables,
} from "../../../../API";
import { useCustomerIdGuard } from "../../../hooks/useCustomerIdGuard";
import { GET_NODES_WITHOUT_DEVICE_BY_CUSTOMER } from "../../../operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../variables/common";
import { errorNotification } from "../../../variables/notification";

export const useGetNodesWithoutDeviceByCustomer = () => {
  const selectedCustomerId = useCustomerIdGuard();

  useEffect((): void => {
    if (selectedCustomerId) {
      fetchNodes(selectedCustomerId);
    }
  }, [selectedCustomerId]);

  const [getNodes, { data, loading, fetchMore, networkStatus }] = useLazyQuery<
    GetNodesWithoutDeviceByCustomerQuery,
    GetNodesWithoutDeviceByCustomerQueryVariables
  >(GET_NODES_WITHOUT_DEVICE_BY_CUSTOMER, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const fetchNodes = async (customerId: string, nextToken?: string) => {
    try {
      let nextPageToken = null;

      const response = await getNodes({
        variables: {
          limit: DEFAULT_LIMIT_COUNT,
          customerId,
          nextToken,
        },
      });

      const responseNextToken =
        response?.data?.getNodesWithoutDeviceByCustomer?.nextToken;

      if (responseNextToken) {
        nextPageToken = responseNextToken;

        // Fetch more results if nextToken is available
        while (nextPageToken) {
          const fetchMoreResults: ApolloQueryResult<GetNodesWithoutDeviceByCustomerQuery> =
            await fetchMore({
              variables: {
                limit: DEFAULT_LIMIT_COUNT,
                customerId,
                nextToken: nextPageToken,
              },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) {
                  return prev;
                }

                const uniqueNodes = uniqBy(
                  [
                    ...(prev.getNodesWithoutDeviceByCustomer?.items ?? []),
                    ...(fetchMoreResult.getNodesWithoutDeviceByCustomer
                      ?.items ?? []),
                  ],
                  item => item?.id
                );

                return {
                  getNodesWithoutDeviceByCustomer: {
                    ...fetchMoreResult.getNodesWithoutDeviceByCustomer,
                    items: uniqueNodes,
                  },
                };
              },
            });

          const fetchMoreResultsNextToken =
            fetchMoreResults?.data?.getNodesWithoutDeviceByCustomer?.nextToken;

          if (fetchMoreResultsNextToken) {
            nextPageToken = fetchMoreResultsNextToken;
          } else {
            nextPageToken = null;
          }
        }
      }
    } catch (error) {
      console.error(`Error while fetching devices: ${error}`);

      errorNotification();
    }
  };

  return {
    data,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
  };
};
